/**
 * tableSchemaLoader.ts
 *
 * Registers a table schema DI loader with form-engine's blockLoaderRegistry.
 * Table schemas live in src/apps/customer/pages/{page}/schema.ts (app layer).
 * This file globs them at build time and provides a lookup function keyed by
 * "page/exportName" (e.g. "participants/participantTable").
 *
 * Import as a side-effect in app.tsx:
 *   import '@apps/customer/tableSchemaLoader';
 */

import { setTableLoader } from '@packages/compose/registry';

const PAGE_SCHEMAS = import.meta.glob(
  '/src/apps/customer/pages/*/schema.ts',
  { eager: true },
) as Record<string, Record<string, unknown>>;

function isTableSchema(v: unknown): v is Record<string, unknown> {
  return (
    typeof v === 'object' &&
    v !== null &&
    'metadata' in v &&
    typeof (v as Record<string, unknown>).metadata === 'object'
  );
}

const tableMap = new Map<string, Record<string, unknown>>();

for (const [modPath, mod] of Object.entries(PAGE_SCHEMAS)) {
  const match = modPath.match(/\/pages\/([^/]+)\/schema\.ts$/);
  if (!match) continue;
  const page = match[1];

  for (const [exportName, value] of Object.entries(mod)) {
    if (!isTableSchema(value)) continue;
    tableMap.set(`${page}/${exportName}`, value);
  }
}

/**
 * Register the table schema loader into the compose engine's DI registry.
 *
 * MUST be called explicitly in the application entry point (index.tsx).
 */
export function initTableSchemaLoader(): void {
  setTableLoader(async (blockId: string) => {
    let key = blockId;
    if (key.startsWith('table:')) key = key.slice('table:'.length);
    else if (key.startsWith('wealthlane://tables/')) key = key.slice('wealthlane://tables/'.length);
    return tableMap.get(key) ?? null;
  });
}
